import React, { Component } from "react";
import PropTypes from "prop-types";
import { Col, Collapse } from "react-bootstrap";
import { connect } from "react-redux";
import { replace } from "react-router-redux";
import _ from "lodash";
import {
  AWB_NUMBER,
  BUYER_CREDIT_PCT,
  COST,
  MARKUP,
  MINIMUM_EXCHANGE_SRP,
  QUANTITY,
  SETTINGS
} from "APP_ROOT/constants";
import classNames from "classnames";
import {
  formValueSelector,
  Field,
  Fields,
  change,
  getFormInitialValues
} from "redux-form";
import api from "APP_ROOT/api";
import { AS_IS, INCOMING_LINE_ITEMS } from "MODULES/IncomingQuotes/constants";
import {
  ALTERNATE_ID,
  AMOUNT_INSURED,
  CANCELLATION_REASON,
  CANCELLATION_REASONS,
  CAP_AMOUNT,
  CAP_TYPE,
  CAP_TYPES,
  CONTAINER_FEE,
  DIM_WEIGHT,
  EVALUATION_PERIOD,
  EXPECTED_SERIAL_NUMBER,
  EXTERNAL_NOTE,
  HAZMAT_FEE,
  INSURANCE_TYPE,
  INTERNAL_NOTE,
  PERCENT_INSURED,
  PREFERRED_REPAIR_STATION_ID
} from "MODULES/PurchaseOrders/constants";
import {
  BIO_HAZMAT,
  DOM,
  HAZMAT,
  TOTAL_CYCLES,
  TOTAL_HOURS
} from "MODULES/parts/constants";
import { BuyerField, CoBuyersFields } from "MODULES/PurchaseOrders/shared";
import * as actions from "MODULES/PurchaseOrders/actions";
import * as partsActions from "MODULES/parts/actions";
import {
  form,
  Form as AppForm,
  AutoCompleteField,
  AutoCompleteUserInputField,
  Check,
  FormInput,
  FormRow,
  CurrencyField,
  MonthYearField,
  NumberInput,
  Select,
  TextArea,
  CheckGroup
} from "MODULES/shared/components/Forms";
import { CONSIGNMENT, TAG_DATE_CONDITIONS } from "MODULES/Receiving/constants";
import PartSummary from "MODULES/ManagedRepairOrders/shared/PartSummary";
import {
  ACCOUNTING,
  CO_BUYER_ID,
  QUALITY_CONTROL,
  REPAIR,
  SALES,
  THIRD_BUYER_ID,
  USER
} from "MODULES/users/constants";
import { properCase } from "LIB/formatters/text";
import {
  CONDITION,
  SUGGESTED_RETAIL,
  TAGGED_BY,
  TRACE_TYPE,
  EXCHANGE_SRP,
  TRACED_TO,
  PART_ID,
  EXPIRATION,
  LEAD_TIME,
  TAG_DATE,
  TAGGED_BY_ID
} from "MODULES/inventories/constants";
import { toOptions } from "LIB/forms";
import { ACTIVE, APPROVED, CREDIT_HOLD } from "MODULES/companies/constants";
import { MOD_TO, MOD_TO_ID } from "MODULES/RepairOrders/constants";
import ToolsUsedDropDown from "MODULES/Locations/components/ToolsUsedDropDown";
import InvoiceSection from "MODULES/Orders/shared/InvoiceSection";
import { hasAnyOfRoles, hasRole } from "MODULES/users/helpers";
import { DashedSeparator, Warning } from "MODULES/shared/components";
import { MANAGED_REPAIR_ORDER } from "MODULES/ManagedRepairOrders/constants";
import validate from "../validate";

const formName = "editMroLineItemForm";

export const SRP_CONDITION_OPTIONS = [
  { value: "overhauled", text: "Overhauled" },
  { value: "repaired", text: "Repaired" },
  { value: "inspected", text: "Inspected" },
  { value: "new_item", text: "New" },
  { value: "as_removed", text: "As Removed" },
  { value: "beyond_econ_repair", text: "Beyond Econ Repair" }
];

function InsuranceTypeField({
  amount_insured: amountInsured,
  percent_insured: percentInsured,
  insurance_type: insuranceType,
  disabled
}) {
  const activePercent = classNames("btn btn-default", {
    active: insuranceType.input.value === "Percent"
  });
  const activeAmount = classNames("btn btn-default", {
    active: insuranceType.input.value === "Amount"
  });
  const activeNone = classNames("btn btn-default", {
    active: insuranceType.input.value === "None"
  });

  const handleInsuranceTypeChange = newVal => () => {
    if (disabled) return;
    insuranceType.input.onChange(newVal);
    if (newVal === "Amount") {
      percentInsured.input.onChange(null);
    } else if (newVal === "Percent") {
      amountInsured.input.onChange(null);
    } else {
      amountInsured.input.onChange(null);
      percentInsured.input.onChange(null);
    }
  };

  return (
    <div className="form-group" style={{ marginBottom: 20 }}>
      <label className="control-label" htmlFor="insurance_type">
        Insurance
      </label>
      <div className="btn-group btn-group-justified" role="group">
        <div
          className={activeNone}
          disabled={disabled}
          onClick={handleInsuranceTypeChange("None")}
        >
          <i className="fa fa-lg fa-check-circle" />
          &nbsp;&nbsp;Do Not Insure
        </div>
        <div
          className={activeAmount}
          disabled={disabled}
          onClick={handleInsuranceTypeChange("Amount")}
        >
          <i className="fa fa-lg fa-check-circle" />
          &nbsp;&nbsp;Amount
        </div>
        <div
          className={activePercent}
          disabled={disabled}
          onClick={handleInsuranceTypeChange("Percent")}
        >
          <i className="fa fa-lg fa-check-circle" />
          &nbsp;&nbsp;Percent
        </div>
      </div>
    </div>
  );
}

InsuranceTypeField.propTypes = {
  amount_insured: PropTypes.instanceOf(Object),
  percent_insured: PropTypes.instanceOf(Object),
  insurance_type: PropTypes.instanceOf(Object),
  disabled: PropTypes.bool
};

function AmountInsuredFields({ amount, disabled }) {
  return (
    <Col md={4}>
      <CurrencyField
        name={amount.input.name}
        label="Insurance Amount"
        disabled={disabled}
        required
      />
    </Col>
  );
}

AmountInsuredFields.propTypes = {
  amount: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool
};

export function DOMIsRequiredWarning() {
  return <Warning>Part is DOM Sensitive. DOM Required.</Warning>;
}

export function UnauthorizedRepairStationSelectedWarning() {
  return (
    <Warning>
      Note: This Repair Station is not approved. Unless you are reserving this
      unit for repair to a different repair station, PO will require management
      approval before you are able to send PO.
    </Warning>
  );
}

export function UnauthorizedPreferredRepairStationSelectedWarning() {
  return (
    <Warning>
      Note: This Repair Station is not approved. PO will require management
      approval before you are able to send PO.
    </Warning>
  );
}

function PercentInsuredFields({ percent }) {
  return (
    <Col md={12}>
      <FormRow>
        <Col md={4}>
          <Field
            name={percent.input.name}
            label="Insurance Percent"
            component={NumberInput}
            parse={value => value && parseInt(value, 10)}
            addon={<i className="fa fa-percent" />}
            required
          />
        </Col>
      </FormRow>
    </Col>
  );
}

PercentInsuredFields.propTypes = {
  percent: PropTypes.instanceOf(Object)
};

function InsuranceFields({
  amount_insured: amountInsured,
  percent_insured: percentInsured,
  insurance_type: insuranceType,
  quantity,
  disabled
}) {
  if (insuranceType.input.value === "Amount") {
    return <AmountInsuredFields amount={amountInsured} disabled={disabled} />;
  } else if (insuranceType.input.value === "Percent") {
    return (
      <PercentInsuredFields
        insuranceType={insuranceType}
        percent={percentInsured}
        quantity={quantity}
        disabled={disabled}
      />
    );
  }

  return null;
}

InsuranceFields.propTypes = {
  amount_insured: PropTypes.instanceOf(Object),
  percent_insured: PropTypes.instanceOf(Object),
  insurance_type: PropTypes.instanceOf(Object),
  disabled: PropTypes.bool,
  quantity: PropTypes.instanceOf(Object)
};

function LineHazmatFields({ hazmat, bio_hazmat, disabled }) {
  return (
    <div className="btn-group btn-group-justified" data-toggle="buttons">
      <Check
        input={hazmat.input}
        label={properCase(HAZMAT)}
        icon="fa-warning"
        disabled={disabled}
      />
      <Check
        input={bio_hazmat.input}
        label={properCase(BIO_HAZMAT)}
        icon="fa-flask"
        disabled={disabled}
      />
    </div>
  );
}

LineHazmatFields.propTypes = {
  hazmat: PropTypes.instanceOf(Object).isRequired,
  bio_hazmat: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool
};

export function AsIsField({
  as_is: asIs,
  evaluation_period: evaluationPeriod,
  disabled
}) {
  const handleChange = val => {
    asIs.input.onChange(val);
    if (val === true) {
      evaluationPeriod.input.onChange(0);
    } else {
      evaluationPeriod.input.onChange(30);
    }
  };

  const input = { ...asIs.input, onChange: handleChange };
  return (
    <div className="btn-group btn-group-justified" data-toggle="buttons">
      <Check
        input={input}
        label={properCase(AS_IS)}
        icon="fa-check-circle"
        disabled={disabled}
      />
    </div>
  );
}

AsIsField.propTypes = {
  as_is: PropTypes.instanceOf(Object).isRequired,
  evaluation_period: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool
};

export class Form extends Component {
  static propTypes = {
    getFieldValue: PropTypes.func.isRequired,
    setTaggedById: PropTypes.func.isRequired,
    setTaggedByHold: PropTypes.func.isRequired,
    showBuyers: PropTypes.bool,
    buyerCreditPct: PropTypes.string,
    cap_type: PropTypes.string,
    condition: PropTypes.string,
    cost: PropTypes.string,
    currentUser: PropTypes.instanceOf(Object),
    id: PropTypes.number,
    lineItem: PropTypes.shape({
      co_buyer: PropTypes.shape({}),
      third_buyer: PropTypes.shape({}),
      part: PropTypes.shape({}),
      part_id: PropTypes.string
    }),
    quantity: PropTypes.number,
    renderFooter: PropTypes.func,
    resetAlternatePartField: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    searchAlternates: PropTypes.func.isRequired,
    selectedLineItem: PropTypes.instanceOf(Object),
    searchBuyers: PropTypes.func.isRequired,
    searchParts: PropTypes.func.isRequired,
    searchRepairStations: PropTypes.func.isRequired,
    setHeavyPackage: PropTypes.func.isRequired,
    setHazardFields: PropTypes.func.isRequired,
    setPartId: PropTypes.func.isRequired,
    shippingType: PropTypes.string,
    domLifeYears: PropTypes.number,
    llp: PropTypes.bool,
    selectedPart: PropTypes.instanceOf(Object).isRequired,
    setSelectedPart: PropTypes.func.isRequired,
    setPreferredRepairStationHold: PropTypes.func.isRequired,
    resetPreferredRepairStation: PropTypes.func.isRequired
  };

  static defaultProps = {
    modalMode: true,
    showBuyers: true
  };

  state = {
    alternateOptions: []
  };

  componentWillMount() {
    if (this.props.lineItem) {
      const id = this.props.lineItem.part_id;
      const part = this.props.lineItem.part;
      const buyer = this.props.lineItem.buyer;
      const coBuyer = this.props.lineItem.co_buyer;
      const thirdBuyer = this.props.lineItem.third_buyer;

      if (part) {
        this.props.setHazardFields(part);
        this.props.setHeavyPackage(part);
        this.fetchAlternates(part.id);
      }

      this.props.setPartId(id);
      this.props.setSelectedPart(part);
      this.setState({
        selectedBuyer: buyer,
        selectedCoBuyer: coBuyer,
        selectedThirdBuyer: thirdBuyer
      });
    }

    this.setState({
      selectedReserveForSeller: this.props.currentUser
    });

    if (this.props.selectedLineItem) {
      const {
        buyer,
        part,
        mod_to: modTo,
        co_buyer,
        third_buyer,
        preferred_repair_station: preferredRepairStation,
        reserve_for_seller: reserveForSeller
      } = this.props.selectedLineItem;

      this.setState({
        selectedModTo: modTo,
        selectedPart: part,
        selectedBuyer: buyer,
        selectedCoBuyer: co_buyer,
        selectedThirdBuyer: third_buyer,
        selectedPreferredRepairStation: preferredRepairStation,
        selectedReserveForSeller: reserveForSeller || this.props.currentUser
      });

      this.props.setSelectedPart(part);
      this.fetchAlternates(part.id);
    }
  }

  fetchAlternates = partId => {
    if (!partId) return;
    this.props.searchAlternates(partId).then(results =>
      this.setState({
        alternateOptions: results.map(item => ({
          text: item.name,
          value: item.id
        }))
      })
    );
  };

  handlePartSelected = part => {
    this.props.setSelectedPart(part);
    this.props.resetAlternatePartField();
    if (part) {
      this.props.setHazardFields(part);
      this.props.setHeavyPackage(part);
      this.fetchAlternates(part.id);
    }
  };

  inventoryLocked(field) {
    const { selectedLineItem } = this.props;
    if (!selectedLineItem) return false;

    const received =
      selectedLineItem.received || selectedLineItem.quantity_received > 0;

    const defaultPermission = received || selectedLineItem.cancelled;
    if (!field || selectedLineItem.cancelled) {
      return defaultPermission;
    }

    return {
      [QUANTITY]: selectedLineItem.cancelled,
      [SUGGESTED_RETAIL]: selectedLineItem.received,
      [COST]:
        selectedLineItem.consignment_type !== CONSIGNMENT && defaultPermission
    }[field];
  }

  searchCompanies = filter =>
    api.companies
      .search({
        repair_station: true,
        filter,
        status: [ACTIVE, CREDIT_HOLD].join(","),
        page: 1,
        limit: 20
      })
      .then(resp => resp.data.results);

  handleTaggedBy = company => {
    const { setTaggedById, setTaggedByHold } = this.props;
    if (company && company.id) {
      setTaggedById(company.id);
      setTaggedByHold(company.repair_station_status !== APPROVED);
    } else {
      setTaggedByHold(false);
    }
  };

  handlePreferredRepairStation = company => {
    const { setPreferredRepairStationHold } = this.props;

    if (company && company.id) {
      setPreferredRepairStationHold(company.repair_station_status !== APPROVED);
    }
  };

  resetPreferredRepairStation = () => {
    const { resetPreferredRepairStation } = this.props;

    resetPreferredRepairStation();

    this.setState({ selectedPreferredRepairStation: null });
  };

  toggleBuyer = buyer => this.setState(buyer);

  render() {
    const {
      getFieldValue,
      buyerCreditPct,
      currentUser,
      capType,
      condition,
      containerFee,
      cost,
      quantity,
      searchBuyers,
      searchParts,
      searchRepairStations,
      selectedLineItem,
      setFieldValue,
      hazmatFee,
      markedHazardous,
      modalMode,
      renderFooter,
      selectedPart,
      domLifeYears,
      llp,
      modTo,
      dom
    } = this.props;

    const buyerCreditPctOptions = [
      { text: "75%", value: "75.0" },
      { text: "50%", value: "50.0" },
      { text: "25%", value: "25.0" }
    ];
    const cancelled = selectedLineItem && selectedLineItem.cancelled;
    const {
      selectedModTo,
      selectedBuyer,
      selectedCoBuyer,
      selectedThirdBuyer,
      selectedPreferredRepairStation,
      alternateOptions
    } = this.state;

    const locked = this.inventoryLocked();
    const leadTimeLocked = hasRole(currentUser, QUALITY_CONTROL)
      ? false
      : locked;
    const formProps = {
      ...this.props,
      editable:
        !(
          locked &&
          this.inventoryLocked(QUANTITY) &&
          this.inventoryLocked(SUGGESTED_RETAIL)
        ) || cancelled
    };

    const taggedByHold = getFieldValue("tagged_by_hold");
    const preferredRepairStationHold = getFieldValue(
      "preferred_repair_station_hold"
    );

    return (
      <AppForm {...formProps} modal={modalMode}>
        <AppForm.Body>
          <div className="row">
            <Col md={modalMode ? 8 : 12}>
              <div
                className="row row-col-gutters-half"
                style={{ marginTop: "26px" }}
              >
                <Col md={4}>
                  <Field
                    name={PART_ID}
                    label="Part #"
                    placeholder="Type a part's number..."
                    component={AutoCompleteField}
                    search={searchParts}
                    selectedItem={selectedPart}
                    formatItem={result => result && result.part_number}
                    onItemSelected={i => i && this.handlePartSelected(i)}
                    disabled={locked}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Field
                    name={ALTERNATE_ID}
                    label="Buy As Alternate"
                    placeholder="Type a part's number..."
                    component={Select}
                    options={alternateOptions}
                    addon={<i className="fa fa-exchange" />}
                    disabled={alternateOptions.length === 0}
                  />
                </Col>
              </div>
              <DashedSeparator />
              <FormRow>
                <Col md={3}>
                  <Field
                    name={CONDITION}
                    component={Select}
                    options={SRP_CONDITION_OPTIONS}
                    label={properCase(CONDITION)}
                    disabled={locked}
                    required
                  />
                </Col>
                <Col md={2}>
                  <Field
                    name={QUANTITY}
                    component={FormInput}
                    label="QTY"
                    type="number"
                    disabled={this.inventoryLocked(QUANTITY)}
                    required
                    onBlur={(_ev, qty) => {
                      if (Number(qty) !== 1) {
                        setFieldValue(EXPECTED_SERIAL_NUMBER)(null);
                      }
                    }}
                    inputProps={{
                      min: 0
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Field
                    label={properCase(MARKUP)}
                    name={MARKUP}
                    component={NumberInput}
                    addon={<i className="fa fa-percent" />}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 100
                    }}
                    disabled={this.inventoryLocked(MARKUP)}
                    required
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={3}>
                  <Field
                    name={EXPIRATION}
                    label="Expiration Date"
                    component={FormInput}
                    type="date"
                    disabled={locked}
                  />
                </Col>
              </FormRow>
              <DashedSeparator />
              <FormRow>
                <Col md={5}>
                  <div className="form-group">
                    <label htmlFor="warnings" className="control-label">
                      Line Hazmat
                    </label>
                    <br />
                    <Fields
                      names={[HAZMAT, BIO_HAZMAT]}
                      component={LineHazmatFields}
                      disabled={locked}
                    />
                  </div>
                </Col>
                <Col md={2}>
                  <div className="form-group">
                    <label htmlFor={AS_IS} className="control-label">
                      As Is
                    </label>
                    <br />
                    <Fields
                      names={[AS_IS, CONDITION, EVALUATION_PERIOD]}
                      component={AsIsField}
                      disabled={locked}
                    />
                  </div>
                </Col>
                <Col md={5}>
                  <CheckGroup title="Heavy Package / DIM Weight" justified>
                    <Field
                      name={DIM_WEIGHT}
                      component={Check}
                      label="Hvy Pkg / DIM Weight"
                      icon="fa-check-circle"
                      disabled={locked}
                    />
                  </CheckGroup>
                </Col>
              </FormRow>
              {selectedModTo && (
                <Warning>
                  SRP Info will be applied to PN {selectedModTo.part_number}
                </Warning>
              )}
              {taggedByHold && <UnauthorizedRepairStationSelectedWarning />}
              {selectedPart.dom_sensitive &&
                _.isEmpty(dom) && <DOMIsRequiredWarning />}
              <hr className="hr-xs hr-dashed" />
              <FormRow>
                <Col md={4}>
                  <MonthYearField
                    name={TAG_DATE}
                    label={properCase(TAG_DATE)}
                    component={FormInput}
                    required={TAG_DATE_CONDITIONS.includes(condition)}
                    disabled={locked}
                  />
                </Col>
                <Col md={4}>
                  <Field
                    name={TAGGED_BY}
                    label={properCase(TAGGED_BY)}
                    placeholder="Search by a company's name"
                    component={AutoCompleteUserInputField}
                    required={TAG_DATE_CONDITIONS.includes(condition)}
                    search={this.searchCompanies}
                    selectedItem={
                      selectedLineItem && selectedLineItem.tagged_by
                    }
                    handleTaggedBy={result => this.handleTaggedBy(result)}
                    formatItem={result =>
                      (typeof result === "object" ? result.name : result) || ""}
                    disabled={locked}
                  />
                </Col>
                <Col md={4}>
                  <ToolsUsedDropDown
                    disabled={locked}
                    selectedLineItem={selectedLineItem}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={4}>
                  <MonthYearField
                    name="dom"
                    label="DOM"
                    component={FormInput}
                    required={
                      (domLifeYears && llp) || selectedPart.dom_sensitive
                    }
                    disabled={locked}
                  />
                </Col>
                <Col md={4}>
                  <Field
                    name={TRACE_TYPE}
                    component={Select}
                    options={toOptions(INCOMING_LINE_ITEMS.traceTypes)}
                    label="Expected Trace Type"
                    disabled={locked}
                  />
                </Col>
                <Col md={4}>
                  <Field
                    name={TRACED_TO}
                    component={FormInput}
                    label="Expected Trace To"
                    disabled={locked}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={4}>
                  <Field
                    name={LEAD_TIME}
                    component={FormInput}
                    label="Lead Time (Days)"
                    disabled={leadTimeLocked}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Field
                    name={EXPECTED_SERIAL_NUMBER}
                    label="Expected Serial #"
                    component={FormInput}
                    disabled={
                      (selectedLineItem && selectedLineItem.received) ||
                      Number(quantity) !== 1
                    }
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={3}>
                  <CheckGroup justified title={properCase(MOD_TO)}>
                    <Field
                      name={MOD_TO}
                      label={properCase(MOD_TO)}
                      icon="fa-check-circle"
                      component={Check}
                      disabled={locked}
                    />
                  </CheckGroup>
                </Col>

                <Collapse in={modTo || false} unmountOnExit>
                  <Col md={4}>
                    <Field
                      name={MOD_TO_ID}
                      label={properCase(MOD_TO)}
                      placeholder="Type a part's number..."
                      selectedItem={selectedModTo}
                      component={AutoCompleteField}
                      onItemSelected={item =>
                        item && this.setState({ selectedModTo: item })}
                      search={searchParts}
                      formatItem={result => result && result.part_number}
                      disabled={locked}
                    />
                  </Col>
                </Collapse>
              </FormRow>
              {selectedPart &&
                selectedPart.llp && (
                  <FormRow>
                    <Col md={3}>
                      <Field
                        name={TOTAL_HOURS}
                        label={properCase(TOTAL_HOURS)}
                        component={NumberInput}
                        roundDigits={0}
                      />
                    </Col>
                    <Col md={3}>
                      <Field
                        name={TOTAL_CYCLES}
                        label={properCase(TOTAL_CYCLES)}
                        component={NumberInput}
                        roundDigits={0}
                      />
                    </Col>
                  </FormRow>
                )}
              {this.props.showBuyers && (
                <React.Fragment>
                  <DashedSeparator />
                  <h4>Buyers</h4>
                  <FormRow>
                    <Col md={5}>
                      <BuyerField
                        disabled={locked}
                        searchBuyers={searchBuyers}
                        selectedBuyer={selectedBuyer}
                        onItemSelected={buyer =>
                          this.setState({ selectedBuyer: buyer })}
                      />
                    </Col>
                    <Col md={3}>
                      <Field
                        name={BUYER_CREDIT_PCT}
                        className="text-right"
                        component={Select}
                        options={buyerCreditPctOptions}
                        prompt={false}
                        label="Buyer Credit"
                        disabled={locked}
                        required
                      />
                    </Col>
                  </FormRow>
                  <FormRow>
                    <CoBuyersFields
                      toggleBuyer={this.toggleBuyer}
                      buyerCreditPct={buyerCreditPct}
                      selectedCoBuyer={selectedCoBuyer}
                      selectedThirdBuyer={selectedThirdBuyer}
                      numberOfBuyers={
                        [
                          getFieldValue(CO_BUYER_ID),
                          getFieldValue(THIRD_BUYER_ID)
                        ].filter(s => s).length + 1
                      }
                      searchBuyers={searchBuyers}
                      disabled={locked}
                    />
                  </FormRow>
                </React.Fragment>
              )}
              <InvoiceSection
                hidden={
                  !hasAnyOfRoles(currentUser, [
                    ACCOUNTING,
                    QUALITY_CONTROL,
                    REPAIR,
                    SALES
                  ])
                }
                orderType={MANAGED_REPAIR_ORDER}
              />
              <DashedSeparator />
              {preferredRepairStationHold && (
                <UnauthorizedPreferredRepairStationSelectedWarning />
              )}
              <h4>Shipping & Repairs</h4>
              <FormRow>
                {selectedLineItem && (
                  <Col md={6}>
                    <Field
                      name={AWB_NUMBER}
                      component={FormInput}
                      label="AWB #"
                      disabled={selectedLineItem.received}
                    />
                  </Col>
                )}
                <Col md={3}>
                  <CurrencyField
                    name={CONTAINER_FEE}
                    label="Container/Crate Fee"
                    disabled={locked}
                  />
                </Col>
                <Col md={3}>
                  <CurrencyField
                    name={HAZMAT_FEE}
                    label={properCase(HAZMAT_FEE)}
                    disabled={locked}
                    required={markedHazardous}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={6}>
                  <Field
                    name={PREFERRED_REPAIR_STATION_ID}
                    label="Suggested Repair Station"
                    placeholder="Type a vendor's name or code..."
                    component={AutoCompleteField}
                    search={searchRepairStations}
                    selectedItem={selectedPreferredRepairStation}
                    formatItem={result => result && result.name}
                    disabled={locked}
                    required
                    watch={selectedPreferredRepairStation}
                    onItemSelected={this.handlePreferredRepairStation}
                  />
                </Col>
                <Col md={6}>
                  <Field
                    name={EVALUATION_PERIOD}
                    type="number"
                    component={FormInput}
                    disabled={locked}
                    label="Eval. Period"
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={6}>
                  <Field
                    name={CAP_TYPE}
                    className="text-right"
                    component={Select}
                    options={toOptions(CAP_TYPES)}
                    label="Repair/Overhaul Cap"
                  />
                </Col>
                {capType && (
                  <Col md={6}>
                    <CurrencyField
                      name={CAP_AMOUNT}
                      label={`${CAP_TYPES[capType].label} Cap Amount`}
                      disabled={locked}
                    />
                  </Col>
                )}
              </FormRow>
              <FormRow>
                <Col md={12}>
                  <Fields
                    names={[AMOUNT_INSURED, PERCENT_INSURED, INSURANCE_TYPE]}
                    component={InsuranceTypeField}
                    disabled={locked}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Fields
                  names={[
                    AMOUNT_INSURED,
                    PERCENT_INSURED,
                    INSURANCE_TYPE,
                    QUANTITY
                  ]}
                  component={InsuranceFields}
                  disabled={locked}
                />
              </FormRow>
              <DashedSeparator />
              <h4>Notes</h4>
              <FormRow>
                <Col md={6}>
                  <Field
                    name={INTERNAL_NOTE}
                    label={properCase(INTERNAL_NOTE)}
                    component={TextArea}
                    rows={5}
                    disabled={locked}
                  />
                </Col>
                <Col md={6}>
                  <Field
                    name={EXTERNAL_NOTE}
                    label={properCase(EXTERNAL_NOTE)}
                    component={TextArea}
                    rows={5}
                    disabled={locked}
                  />
                </Col>
              </FormRow>
              {cancelled && (
                <FormRow>
                  <Col md={6}>
                    <Field
                      name={CANCELLATION_REASON}
                      className="text-right"
                      component={Select}
                      options={CANCELLATION_REASONS}
                      prompt={false}
                      label={properCase(CANCELLATION_REASON)}
                    />
                  </Col>
                </FormRow>
              )}
            </Col>
            {modalMode && (
              <Col md={4}>
                <PartSummary
                  part={selectedPart}
                  cost={cost}
                  quantity={quantity}
                  lineItem={this.props.selectedLineItem}
                  hazmatFee={hazmatFee}
                  containerFee={containerFee}
                />
              </Col>
            )}
          </div>
        </AppForm.Body>
        {renderFooter && renderFooter(formProps)}
      </AppForm>
    );
  }
}

const selector = formValueSelector(formName);

export default connect(
  state => ({
    getFieldValue: field => selector(state, field),
    getInitialFieldValue: field =>
      _.get(getFormInitialValues(formName)(state), field),
    buyerCreditPct: selector(state, BUYER_CREDIT_PCT),
    capType: selector(state, CAP_TYPE),
    cost: selector(state, COST),
    quantity: selector(state, QUANTITY),
    condition: selector(state, CONDITION),
    minExchangeSrp: state.session.getIn([SETTINGS, MINIMUM_EXCHANGE_SRP]),
    exchangeSRP: selector(state, EXCHANGE_SRP),
    asIs: selector(state, AS_IS),
    currentUser: state.session.get(USER).toJS(),
    hazmatFee: selector(state, HAZMAT_FEE),
    containerFee: selector(state, CONTAINER_FEE),
    markedHazardous: selector(state, HAZMAT),
    modTo: selector(state, MOD_TO),
    dom: selector(state, DOM),
    getField: field => selector(state, field)
  }),
  dispatch => ({
    resetAlternatePartField: () =>
      dispatch(change(formName, ALTERNATE_ID, null)),
    setPartId: id => dispatch(change(formName, PART_ID, id)),
    setHazardFields: part => {
      dispatch(change(formName, BIO_HAZMAT, part.bio_hazmat));
      dispatch(change(formName, HAZMAT, part.hazmat));
    },
    setHeavyPackage: part =>
      dispatch(change(formName, DIM_WEIGHT, part.dim_weight)),
    searchAlternates: partId => dispatch(actions.searchAlternates(partId)),
    searchBuyers: filter => dispatch(actions.searchBuyers(filter)),
    searchParts: filter => dispatch(partsActions.searchPoParts(filter)),
    searchRepairStations: filter =>
      dispatch(actions.searchRepairStations(filter)),
    resetForm: id => {
      dispatch(replace(`/managed-repair-orders/${id}`));
    },
    setFieldValue: field => val => dispatch(change(formName, field, val)),
    setTaggedById: id => dispatch(change(formName, TAGGED_BY_ID, id)),
    setTaggedByHold: hold => dispatch(change(formName, "tagged_by_hold", hold)),
    setPreferredRepairStationHold: hold =>
      dispatch(change(formName, "preferred_repair_station_hold", hold)),
    resetPreferredRepairStation: () =>
      dispatch(change(formName, "preferred_repair_station_id", ""))
  })
)(
  form({
    form: formName,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/ManagedRepairOrders/Detail/LineItemsTable/Edit/Form.jsx